// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import Notify from "@hardwoods/App/Components/Notify";
import XngTooltip from "@hardwoods/App/Components/XngTooltip/XngTooltip";
import addAch from "@hardwoods/App/Handlers/XngAch/AddAch";
import getBankName from "@hardwoods/App/Handlers/XngAch/GetBankName";
import loadAch from "@hardwoods/App/Handlers/XngAch/LoadAch";
import setSelectedAch from "@hardwoods/App/Handlers/XngAch/SetSelectedAch";
import setShowAchErrors from "@hardwoods/App/Handlers/XngAch/SetShowAchErrors";
import setShowAchModal from "@hardwoods/App/Handlers/XngAch/SetShowAchModal";
import { AchParameter } from "@hardwoods/App/Services/AchService";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import {
    getAchs,
    getBankNames,
    getIsAchModalOpen,
    getIsloading,
    getSelectedAch,
    getShowAchError,
} from "@hardwoods/App/Store/Selectors/AchSelectors";
import { openLiveChat } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonWrapper } from "@insite/mobius/Button";
import Checkbox, { CheckboxStyle } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const achs = getAchs(state);
    const currentPage = getCurrentPage(state).type;

    return {
        achs,
        hasAch: achs.length > 0,
        isLoading: getIsloading(state),
        bankNames: getBankNames(state),
        selectedAch: getSelectedAch(state),
        isAchModalOpen: getIsAchModalOpen(state),
        showErrors: getShowAchError(state),
        isCheckoutPage: currentPage === "CheckoutReviewAndSubmitPage",
    };
};

const mapDispatchToProps = { loadAch, getBankName, addAch, setSelectedAch, setShowAchErrors, setShowAchModal };

interface OwnProps {
    extendedStyles?: AchDetailsEntryStyles;
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface AchDetailsEntryStyles {
    achDetailsContainer?: GridContainerProps;
    routingNumberGridItem?: GridItemProps;
    routingNumberText?: TextFieldPresentationProps;
    accountNumberGridItem?: GridItemProps;
    accountNumberText?: TextFieldPresentationProps;
    bankNameGridItem?: GridItemProps;
    AccountNicknameGridItem?: GridItemProps;
    bankNameText?: TextFieldPresentationProps;
    accountNicknameText?: TextFieldPresentationProps;
    accountTypeGridItem?: GridItemProps;
    accountTypeSelect?: SelectPresentationProps;
    actionsGridItem?: GridItemProps;
}

export const achDetailsEntryStyles: AchDetailsEntryStyles = {
    achDetailsContainer: {
        gap: 20,
    },
    routingNumberGridItem: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    bankNameGridItem: {
        width: 12,
    },
    AccountNicknameGridItem: {
        width: 12,
    },
    accountNumberGridItem: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    accountTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    actionsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            flex-wrap: wrap;
            margin-top: 20px;

            & > ${CheckboxStyle} {
                margin-right: 16px;
            }
            ${xScreen.lte("xs")} {
                justify-content: space-between;
                & > ${ButtonWrapper}, ${CheckboxStyle} {
                    margin-bottom: 16px;
                    flex: 1 0 auto;
                }
            }
        `,
    },
    accountTypeSelect: {
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
            `,
        },
    },
};

interface AccountTypes {
    key: number;
    value: string;
}

const accountTypes: AccountTypes[] = [
    { key: 0, value: "Checking" },
    { key: 1, value: "Savings" },
];

const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

const AchDetailsEntry = ({
    isLoading,
    bankNames,
    loadAch,
    addAch,
    getBankName,
    achs,
    hasAch,
    setSelectedAch,
    selectedAch,
    setShowAchErrors,
    setShowAchModal,
    isAchModalOpen,
    showErrors,
    extendedStyles,
    isCheckoutPage,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(achDetailsEntryStyles, extendedStyles));
    const [routingNumber, setRoutingNumber] = useState("");
    const [routingNumberError, setRoutingNumberError] = useState("");

    const [accountNumber, setAccountNumber] = useState("");
    const [accountNumberError, setAccountNumberError] = useState("");

    const [accountType, setAccountType] = useState("");
    const [accountTypeError, setAccountTypeError] = useState("");

    const [approvedforACH, setApprovedforACH] = useState(true);

    const [isEditingBank, setIsEditingBank] = useState(false);
    const routingNoTooltipRef = useRef<HTMLDivElement>(null);
    const transitNoTooltipRef = useRef<HTMLDivElement>(null);
    const accountNoTooltipRef = useRef<HTMLDivElement>(null);

    const [errorMessage, setErrorMessage] = useState("");
    const [image, setImage] = useState("");

    const [accountNickname, setAccountNickname] = useState("");

    const [branchId, setBranchId] = useState("");
    const [bankNumber, setBankNumber] = useState("");
    const [branchIdError, setBranchIdError] = useState("");
    const [bankNumberError, setBankNumberError] = useState("");

    useEffect(() => {
        loadAch({
            isCheckoutPage,
        });
    }, []);

    useEffect(() => {
        let ach: AchParameter | undefined = achs[0];
        if (achs.length > 0) {
            const lastFourAccNumber = accountNumber?.slice(accountNumber.length - 4);
            ach = lastFourAccNumber
                ? achs.find(ach => ach.accountNumber === lastFourAccNumber)
                : achs.find(ach => ach.approvedForACHProcessing);
        }
        if (ach || achs[0]) {
            setSelectedAch({ selectedAch: ach || achs[0] });
        }
    }, [achs]);

    useEffect(() => {
        if (showErrors) {
            validateForm();
        }
    }, [showErrors]);

    const onKeyPressHandler = (event: any) => {
        if (!/^\d+$/.test(event.key)) {
            event.preventDefault();
        }
    };

    const validateRoutingNumber = (routingNumber: string) => {
        const routingNumberValid = isNonEmptyString(routingNumber) && routingNumber.length === 9;

        setRoutingNumberError(
            !routingNumberValid
                ? routingNumber.length > 0 && routingNumber.length !== 9
                    ? translate("Routing number must be 9 digits.")
                    : translate("Routing number is required.")
                : "",
        );
        return routingNumberValid;
    };

    const handleRoutingNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRoutingNumber(event.currentTarget.value);
        validateRoutingNumber(event.currentTarget.value);
    };

    const handleBankNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setBankNumber(value);
        validateBankNumber(value);
        setRoutingNumber(`0${value}${branchId}`);
    };

    const handleBranchIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setBranchId(value);
        validateBranchId(value);
        setRoutingNumber(`0${bankNumber}${value}`);
    };

    const validateBankNumber = (bankNumber: string) => {
        const bankNumberValid = isNonEmptyString(bankNumber) && bankNumber.length === 3;

        setBankNumberError(
            !bankNumberValid
                ? bankNumber.length > 0 && bankNumber.length !== 3
                    ? translate("Institution / Bank number must be 3 digits.")
                    : translate("Institution / Bank number is required.")
                : "",
        );
        return bankNumberValid;
    };

    const validateBranchId = (branchId: string) => {
        const branchIdValid = isNonEmptyString(branchId) && branchId.length === 5;

        setBranchIdError(
            !branchIdValid
                ? branchId.length > 0 && branchId.length !== 5
                    ? translate("Transit / Branch Id number must be 5 digits.")
                    : translate("Transit / Branch Id number is required.")
                : "",
        );
        return branchIdValid;
    };

    const handleAccountNicknameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAccountNickname(event.currentTarget.value);
    };

    const validateAccountNumber = (accountNumber: string) => {
        const accountNumberValid = isNonEmptyString(accountNumber) && accountNumber.length >= 5;
        setAccountNumberError(
            !accountNumberValid
                ? accountNumber.length > 0 && accountNumber.length < 5
                    ? translate("Account number must be at least 5 digits.")
                    : translate("Account number is required.")
                : "",
        );
        return accountNumberValid;
    };

    const handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAccountNumber(event.currentTarget.value);
        validateAccountNumber(event.currentTarget.value);
    };

    const validateAccountType = (accountType: string) => {
        const accountTypeValid = isNonEmptyString(accountType);
        setAccountTypeError(!accountTypeValid ? translate("Account type is required.") : "");
        return accountTypeValid;
    };

    const handleAccountTypChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setAccountType(event.currentTarget.value);
        validateAccountType(event.currentTarget.value);
    };

    const validateForm = () => {
        const routingNumberValid = validateRoutingNumber(routingNumber);
        const accountNumberValid = validateAccountNumber(accountNumber);
        const accountTypeValid = validateAccountType(accountType);
        const bankNumberValid = validateBankNumber(bankNumber);
        const branchIdValid = validateBranchId(branchId);
        return (
            routingNumberValid &&
            accountNumberValid &&
            accountTypeValid &&
            (BLUEPRINT_NAME.toLowerCase() === "hardwoodsus" || (bankNumberValid && branchIdValid))
        );
    };

    const handleSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setShowAchErrors({ showErrors: true });
        if (validateForm()) {
            addAch({
                accountNumber,
                bankName: bankNames[routingNumber],
                accountType,
                routingNumber,
                approvedForACHProcessing: approvedforACH,
                bankAcctName: accountNickname,
                onSuccess: () => {
                    setShowAchModal({ isAchModalOpen: false });
                    loadAch({});
                    resetForm();
                },
                onError: error => error && setErrorMessage(error),
            });
        }
    };

    const getToolTip = (imageSrc: string, triggerTarget: React.RefObject<HTMLDivElement>, placement: string) => {
        if (!imageSrc || !imageSrc.length) {
            return null;
        }
        return (
            <XngTooltip
                content={
                    <LazyImage
                        src={imageSrc}
                        css={css`
                            width: 450px;
                            height: 230px;
                            max-width: 100%;
                        `}
                    />
                }
                placement={placement as any}
                maxWidth="380px"
                appendTo="parent"
                triggerTarget={triggerTarget.current}
                getReferenceClientRect={() => triggerTarget.current!.getBoundingClientRect()}
                popperOptions={{
                    modifiers: [
                        {
                            name: "preventOverflow",
                            options: { mainAxis: true },
                        },
                    ],
                }}
                className="ach-bank-entry-hint"
            />
        );
    };

    const resetForm = () => {
        setRoutingNumber("");
        setRoutingNumberError("");
        setAccountNumber("");
        setAccountNumberError("");
        setAccountType("");
        setAccountTypeError("");
        setImage("");
        setAccountNickname("");
        setApprovedforACH(true);
        setIsEditingBank(false);
        setShowAchErrors({ showErrors: false });
        setBankNumberError("");
        setBranchIdError("");
        setBranchId("");
        setBankNumber("");
    };

    const routingNoImage = siteMessage("Routing_No") as string;
    const accountNoImage = siteMessage("Account_No") as string;

    return (
        <>
            <LoadingOverlay loading={isLoading}>
                <GridContainer {...styles.achDetailsContainer}>
                    {hasAch ? (
                        <>
                            <GridItem {...styles.routingNumberGridItem}>
                                {achs.length === 1 ? (
                                    <TextField
                                        label={translate("Account")}
                                        value={selectedAch?.accountNumberLabel}
                                        data-test-selector="account_Number"
                                        readOnly
                                        disabled={true}
                                    />
                                ) : (
                                    <Select
                                        label={translate("Account")}
                                        value={selectedAch?.accountNumber}
                                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                            setSelectedAch({
                                                selectedAch: achs.find(ach => ach.accountNumber === event.target.value),
                                            });
                                        }}
                                        data-test-selector="account_number"
                                    >
                                        {achs.map(ach => (
                                            <option key={ach.accountNumber} value={ach.accountNumber}>
                                                {ach.accountNumberLabel}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            </GridItem>
                            <GridItem {...styles.routingNumberGridItem}>
                                <Checkbox
                                    checked={selectedAch?.approvedForACHProcessing}
                                    disabled={!selectedAch?.accountNumber || selectedAch?.approvedForACHProcessing}
                                    onChange={() => {
                                        resetForm();
                                        setShowAchModal({ isAchModalOpen: true });
                                        setIsEditingBank(true);
                                        setApprovedforACH(true);
                                    }}
                                >
                                    {translate("Use for future orders")}
                                </Checkbox>
                            </GridItem>
                            <GridItem width={12}>
                                <Notify
                                    messageType="info"
                                    body={
                                        <StyledWrapper>
                                            {translate("If you need to delete a bank, please")}
                                            <Link onClick={openLiveChat} color="secondary" type="button">
                                                &nbsp;{translate("contact us")}
                                            </Link>
                                        </StyledWrapper>
                                    }
                                />
                            </GridItem>
                        </>
                    ) : (
                        <GridItem width={12}>
                            <Notify messageType="info" body={siteMessage("HW_InvoicePayments_AddNewACH")} />
                        </GridItem>
                    )}
                    <GridItem width={12}>
                        <Button
                            onClick={() => {
                                resetForm();
                                setShowAchModal({ isAchModalOpen: true });
                            }}
                            type="button"
                            sizeVariant="small"
                        >
                            {translate("Add New Bank")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </LoadingOverlay>
            <Modal
                headline={isEditingBank ? translate("Edit Existing Bank") : translate("Add New Bank")}
                isOpen={isAchModalOpen}
                handleClose={() => {
                    setShowAchModal({ isAchModalOpen: false });
                    resetForm();
                }}
            >
                <GridContainer {...styles.achDetailsContainer} as="form">
                    {isEditingBank && (
                        <GridItem width={12}>
                            <Notify messageType="info" body={translate("Please re-enter your bank information")} />
                        </GridItem>
                    )}
                    {BLUEPRINT_NAME.toLowerCase() === "hardwoodsus" && (
                        <GridItem {...styles.routingNumberGridItem}>
                            <StyledWrapper ref={routingNoTooltipRef}>
                                <TextField
                                    {...styles.routingNumberText}
                                    label={translate("Routing Number")}
                                    value={routingNumber}
                                    onChange={handleRoutingNumberChange}
                                    onBlur={() => {
                                        routingNumber && !bankNames[routingNumber] && getBankName({ routingNumber });
                                    }}
                                    onFocus={() => setImage(routingNoImage)}
                                    required
                                    onKeyPress={onKeyPressHandler}
                                    maxLength={9}
                                    error={showErrors && routingNumberError}
                                    data-test-selector="checkoutReviewAndSubmit_achRoutingNumber"
                                />
                                {getToolTip(image, routingNoTooltipRef, "bottom-end")}
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {BLUEPRINT_NAME === "hardwoods" && (
                        <>
                            <GridItem {...styles.routingNumberGridItem}>
                                <StyledWrapper ref={routingNoTooltipRef}>
                                    <TextField
                                        {...styles.routingNumberText}
                                        label={translate("Institution / Bank Number")}
                                        value={bankNumber}
                                        onChange={handleBankNumberChange}
                                        onBlur={() => {
                                            routingNumber?.length === 9 &&
                                                !bankNames[routingNumber] &&
                                                getBankName({ routingNumber });
                                        }}
                                        onFocus={() => setImage(routingNoImage)}
                                        required
                                        onKeyPress={onKeyPressHandler}
                                        maxLength={3}
                                        error={showErrors && bankNumberError}
                                        data-test-selector="checkoutReviewAndSubmit_achBankNumber"
                                        placeholder={translate("XXX")}
                                    />
                                    {getToolTip(image, routingNoTooltipRef, "bottom-end")}
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.routingNumberGridItem}>
                                <StyledWrapper ref={transitNoTooltipRef}>
                                    <TextField
                                        {...styles.routingNumberText}
                                        label={translate("Transit / Branch ID Number")}
                                        value={branchId}
                                        onChange={handleBranchIdChange}
                                        data-test-selector="checkoutReviewAndSubmit_achBranchId"
                                        error={showErrors && branchIdError}
                                        onFocus={() => setImage(routingNoImage)}
                                        maxLength={5}
                                        required
                                        onBlur={() => {
                                            routingNumber.length === 9 &&
                                                !bankNames[routingNumber] &&
                                                getBankName({ routingNumber });
                                        }}
                                        placeholder={translate("XXXXX")}
                                    />
                                    {getToolTip(image, transitNoTooltipRef, "bottom-end")}
                                </StyledWrapper>
                            </GridItem>
                        </>
                    )}
                    <GridItem {...styles.bankNameGridItem}>
                        <TextField
                            {...styles.bankNameText}
                            label={translate("Bank Name")}
                            value={bankNames[routingNumber] ?? ""}
                            disabled
                            data-test-selector="checkoutReviewAndSubmit_achRoutingNumber"
                        />
                    </GridItem>
                    <GridItem {...styles.AccountNicknameGridItem}>
                        <TextField
                            {...styles.accountNicknameText}
                            label={translate("Account Nickname")}
                            value={accountNickname}
                            onChange={handleAccountNicknameChange}
                            data-test-selector="checkoutReviewAndSubmit_achAccountNickname"
                        />
                    </GridItem>
                    <GridItem {...styles.accountNumberGridItem}>
                        <StyledWrapper ref={accountNoTooltipRef}>
                            <TextField
                                {...styles.accountNumberText}
                                label={translate("Account Number")}
                                value={accountNumber}
                                onChange={handleAccountNumberChange}
                                required
                                minLength={5}
                                maxLength={17}
                                onKeyPress={onKeyPressHandler}
                                onFocus={() => setImage(accountNoImage)}
                                error={showErrors && accountNumberError}
                                data-test-selector="checkoutReviewAndSubmit_achAccountNumber"
                            />
                            {getToolTip(image, accountNoTooltipRef, "top-end")}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.accountTypeGridItem}>
                        <Select
                            {...styles.accountTypeSelect}
                            label={translate("Account Type")}
                            value={accountType}
                            onChange={handleAccountTypChange}
                            required
                            error={showErrors && accountTypeError}
                            data-test-selector="checkoutReviewAndSubmit_achAccountType"
                        >
                            <option value="">{translate("Select Account Type")}</option>
                            {accountTypes.map(at => (
                                <option key={at.key} value={at.value}>
                                    {at.value}
                                </option>
                            ))}
                        </Select>
                        <Checkbox checked={true} disabled={true}>
                            {translate("This order only")}
                        </Checkbox>
                    </GridItem>
                    {errorMessage && (
                        <GridItem width={12}>
                            <Notify messageType="danger" body={errorMessage} />
                        </GridItem>
                    )}
                    <GridItem width={12}>
                        <Notify messageType="info" body={siteMessage("HW_InvoicePayments_AddNewACH")} />
                    </GridItem>
                    <GridItem {...styles.actionsGridItem}>
                        <Checkbox
                            checked={approvedforACH}
                            disabled={approvedforACH}
                            onChange={() => {
                                setApprovedforACH(true);
                            }}
                        >
                            {translate("Use for future orders")}
                        </Checkbox>
                        <Button data-test-selector="add_ACH" onClick={handleSubmit} disabled={isLoading}>
                            {isEditingBank ? translate("Edit Bank") : translate("Add Bank")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AchDetailsEntry);
